import React from 'react';
import styled from 'styled-components';
import {useIntl} from 'react-intl';
import {tokensForThemeProvider, Button, EMPHASIS, FlexRow, Text} from '@forecast-it/design-system';
import Toggle from '../../../../components/inputs/toggle';
import {useId} from '../../../../forecast-app/shared/hooks/useId';
import {NovaVisuallyHidden} from '../shared/novaVisuallyHidden';

export interface NovaDeployAudienceBarProps {
	companyCount: number;
	userCount: number;
	isExpanded: boolean;
	onToggleExpanded: () => void;
	onAddCompany: () => void;
	/**
	 * Whether anyone may be added — false while the agent's saved version cannot run. Disables the add
	 * control and switching the viewer's company on; switching it off stays available.
	 */
	canAdd: boolean;
	/** Whether the viewer's own company currently holds a wholesale grant on this agent. */
	isViewerCompanyActive: boolean;
	onSetViewerCompanyActive: (active: boolean) => void;
	/** A mutation is in flight — the add control and the toggle go inert; the audience shown does not. */
	isSubmitting: boolean;
}

const NovaColor = tokensForThemeProvider.sky.sys.color;

/**
 * The bar is the whole box while the audience is collapsed, and becomes its header once expanded —
 * so it carries the brand blue and square corners, and the container it sits in owns the rounding.
 *
 * The icon recolour is a deliberate override, not a shortcut. The design system has no on-dark
 * button: every `EMPHASIS` resolves its foreground to a dark token, so the `+` and the caret would
 * render near-black on blue and read as disabled. Scoped to this bar so nothing outside it changes.
 */
// Each of the bar's two groups sizes to its own content, so the bar's space-between has free space to
// push them to opposite ends. FlexRow defaults to `flex: 1`, which makes two of them split the width
// in half instead: space-between then has nothing left to distribute, and each group left-aligns
// inside its own half — which reads as the right-hand group floating in mid-bar rather than sitting
// on the edge. `flex={0}` does not fix it, because the default is applied with a falsy check, so 0
// falls through to 1; this has to be a non-empty shorthand.
const BAR_GROUP_FLEX = '0 0 auto';

const Bar = styled.div`
	/* The bar IS the row, so the background, the width and the space-between are all on one element
	   and nothing above it has to cooperate. See BAR_GROUP_FLEX above for the part that actually
	   decides where the two groups land. */
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	width: 100%;
	box-sizing: border-box;

	background: ${NovaColor.brand.primary['1']};
	padding: 10px 16px;

	/* The design system has no on-dark button — every EMPHASIS resolves its foreground to a dark or
	   brand-blue token, so the plus and the caret would be invisible against this blue. Icon paints
	   its colour onto the path rather than the svg, so that is what has to be overridden. Scoped to
	   this bar, so nothing outside it changes. */
	button svg path {
		fill: ${NovaColor.text.content.ondark};
	}
`;

/**
 * The full-width bar above the audience table: the add-a-company control, the expand/collapse caret,
 * the two live counts, and the toggle for whether the viewer's own company is itself in the audience.
 *
 * Carries no opted-out count and no opted-out toggle — the product has no such thing, and showing a
 * zero for it would imply a feature that does not exist.
 */
export const NovaDeployAudienceBar = ({
	companyCount,
	userCount,
	isExpanded,
	onToggleExpanded,
	onAddCompany,
	canAdd,
	isViewerCompanyActive,
	onSetViewerCompanyActive,
	isSubmitting,
}: NovaDeployAudienceBarProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const addLabelId = useId();
	// Switching on is an add; switching off is a removal and is never blocked.
	const isViewerToggleLocked = !canAdd && !isViewerCompanyActive;
	const expandLabelId = useId();

	return (
		<Bar data-cy="nova-deploy-audience-bar">
			<FlexRow flex={BAR_GROUP_FLEX} gap="m" alignItems="center">
				<NovaVisuallyHidden id={addLabelId}>{formatMessage({id: 'nova.deploy.bar.addCompany'})}</NovaVisuallyHidden>
				<Button
					data-cy="nova-deploy-bar-add"
					emphasis={EMPHASIS.HIGH}
					icon="plus"
					aria-labelledby={addLabelId}
					disabled={isSubmitting || !canAdd}
					onPress={onAddCompany}
				/>

				{/* Same control either way — only its accessible name and glyph flip with the state, so a
				    screen reader always announces what pressing it will do next rather than what it is. */}
				<NovaVisuallyHidden id={expandLabelId}>
					{formatMessage({id: isExpanded ? 'nova.deploy.bar.collapse' : 'nova.deploy.bar.expand'})}
				</NovaVisuallyHidden>
				<Button
					data-cy="nova-deploy-bar-expand"
					emphasis={EMPHASIS.HIGH}
					icon={isExpanded ? 'chevronUp' : 'chevronDown'}
					aria-labelledby={expandLabelId}
					onPress={onToggleExpanded}
				/>

				{/* The visible text is a localised message, so it cannot be asserted directly until the
				    message ids are registered with react-intl in tests — the count also travels as a data
				    attribute for that reason, the same pattern the audience modal already uses.
				    The hooks sit on a plain element rather than on `Text` itself: the real `Text`
				    forwards `data-cy` but drops every other `data-*`, so `data-count` never reached the
				    DOM and only appeared to work because the jest design-system double spreads all
				    props. Measured in a browser: `<div size color data-cy class>`, no `data-count`. */}
				<div data-cy="nova-deploy-bar-companies" data-count={companyCount}>
					<Text color="ondark">{formatMessage({id: 'nova.deploy.bar.companies'}, {count: companyCount})}</Text>
				</div>
				<div data-cy="nova-deploy-bar-users" data-count={userCount}>
					<Text color="ondark">{formatMessage({id: 'nova.deploy.bar.individualUsers'}, {count: userCount})}</Text>
				</div>
			</FlexRow>

			<FlexRow flex={BAR_GROUP_FLEX} gap="s" alignItems="center">
				<Text color="ondark">{formatMessage({id: 'nova.deploy.bar.activeForYourCompany'})}</Text>
				{/* web-front's own Toggle (not the design system's) does not forward a data-cy or a click
				    guard to the element it renders, so the test hook and the guard against clicking
				    mid-submit both live on this wrapper rather than on the toggle itself. A real click on
				    the toggle still reaches this handler: it bubbles up from the toggle's own input through
				    this span. It does now forward an accessible name — see toggle.js's own comment — which
				    this passes explicitly rather than relying on the adjacent visible text, since that text
				    has no association (visual proximity is not a screen-reader association) with the
				    checkbox it sits beside. */}
				<span
					data-cy="nova-deploy-bar-viewer-toggle"
					data-checked={String(isViewerCompanyActive)}
					onClick={() => {
						if (!isSubmitting && !isViewerToggleLocked) {
							onSetViewerCompanyActive(!isViewerCompanyActive);
						}
					}}
				>
					<Toggle
						checked={isViewerCompanyActive}
						locked={isSubmitting || isViewerToggleLocked}
						onChange={() => undefined}
						ariaLabel={formatMessage({id: 'nova.deploy.bar.activeForYourCompany'})}
					/>
				</span>
			</FlexRow>
		</Bar>
	);
};
